import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import { TranscriptContainer } from '../models/TranscriptContainer';
import { Segment } from '../models/Segment';
import { Paragraph } from '../models/Paragraph';
import { TextElement } from '../models/TranscriptElement';
import { TranscriptSelectionManager, SelectionEventType } from '../managers/TranscriptSelectionManager';
import { SelectionServiceAdapter } from '../managers/SelectionServiceAdapter';
import SpeakerFormModal from './modals/SpeakerFormModal';
import SpeakerSelectionMenu from './SpeakerSelectionMenu';
import GlobalReplaceModal from './modals/GlobalReplaceModal';
import { useVideoPlayer } from '../hooks/VideoPlayerContext';
import { TranscriptViewProps } from './interfaces';
import TranscriptSegmentWrapper from './TranscriptSegmentWrapper';

// Type guard to check if an element is a text element
const isTextElement = (element: any): element is TextElement => {
  return element && element.type === 'text';
};

// Default theme values for styling
const defaultTheme = {
  colors: {
    text: '#f3e9d7',
    textSecondary: '#a9a9a9',
    background: '#1e1e2c',
    backgroundSecondary: '#2a2a3c',
    border: '#4a4a6a',
    highlight: '#f2d093',
    active: '#4c9aff',
    selection: 'rgba(76, 154, 255, 0.4)',
  },
  fontSizes: {
    small: '12px',
    regular: '14px',
    medium: '16px',
    large: '18px',
  },
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
  },
  borderRadius: {
    sm: '4px',
    md: '8px',
    lg: '12px',
  },
};

// Define the structure of selection data
interface ElementSelectionData {
  id: string;
  segmentId: string;
  paragraphId: string;
  type: string;
  value?: string;
}

/**
 * Component for rendering a transcript (non-virtualized version)
 */
const TranscriptView: React.FC<TranscriptViewProps> = observer(({
  container,
  editorStore,
  onFineAlign,
  onForceAlign,
  className,
  searchTerm = '',
  visibleParagraphIds = null,
}) => {
  // Active paragraph reference
  const activeParaRef = useRef<HTMLDivElement | null>(null);
  
  const [showNewSpeakerModal, setShowNewSpeakerModal] = useState(false);    
  
  // Global text replacement modal state
  const [globalReplaceModalState, setGlobalReplaceModalState] = useState({
    isOpen: false,
    originalText: ''
  });
  
  // Speaker selection menu state
  const [speakerMenuState, setSpeakerMenuState] = useState<{
    isOpen: boolean;
    position: { x: number; y: number };
    selectedElements: ElementSelectionData[];
  }>({
    isOpen: false,
    position: { x: 0, y: 0 },
    selectedElements: []
  });

  // Time tracking state
  const [currentTime, setCurrentTime] = useState(0);
  const currentTimeRef = useRef(0);

  // Access the video player context
  const videoPlayerAPI = useVideoPlayer();
  
  // Flag to prevent seek feedback loops
  const preventFeedbackRef = useRef(false);
  

  const isSegmentActive = (segment: Segment, time: number): boolean => {
    return (segment.startTime <= time && segment.endTime >= time);
  };

  // Check if a paragraph is active based on current time
  const isParagraphActive = (paragraph: Paragraph, time: number): boolean => {
    return (paragraph.startTime <= time && paragraph.endTime >= time);
  };

  const [previousActiveParagraph, setPreviousActiveParagraph] = useState<string>('');

  const scrollToActiveParagraph = useCallback((currentTime: number) => {
    if (!container) {
      return;
    }
    const activeSegment = container.segments.find(seg => isSegmentActive(seg, currentTime));
    const activeParagraph = activeSegment?.paragraphs.find(para => isParagraphActive(para, currentTime));
  
    if (activeParagraph && activeParagraph.id !== previousActiveParagraph) {
      const transcriptContainer = document.querySelector('.transcript-scroll-container');
      const paragraphElement = document.querySelector(`[data-paragraph-id="${activeParagraph.id}"]`);
  
      if (paragraphElement && transcriptContainer) {
        // Get the positions of the container and paragraph
        const containerRect = transcriptContainer.getBoundingClientRect();
        const paragraphRect = paragraphElement.getBoundingClientRect();
  
        // Calculate if paragraph is outside the visible area of the container
        const isAbove = paragraphRect.top < containerRect.top;
        const isBelow = paragraphRect.bottom > containerRect.bottom;
  
        if (isAbove || isBelow) {
          // Scroll within the container instead of the whole page
          transcriptContainer.scrollTop = (paragraphElement as HTMLElement).offsetTop - (transcriptContainer as HTMLElement).offsetTop - (containerRect.height / 2) + (paragraphRect.height / 2);
        }
  
        setPreviousActiveParagraph(activeParagraph.id);
      }
    }
  
  }, [container]);

 
  // Function to jump to a specific timestamp
  const jumpToTimestamp = useCallback((time: number) => {
    // Set flag to prevent feedback loops
    preventFeedbackRef.current = true;

    // Update video player time
    videoPlayerAPI.seekTo(time);

    // Update local state for immediate UI update
    setCurrentTime(time);
    currentTimeRef.current = time;

    // Reset flag after a short delay
    setTimeout(() => {
      preventFeedbackRef.current = false;
      scrollToActiveParagraph(time);
    }, 100);
  }, [videoPlayerAPI]);
  
  // Set up a timer to periodically synchronize the transcript time with the video player time
  useEffect(() => {
    let rafId: number;

    const sync = () => {
      // Skip updates during user-initiated seeks to prevent feedback loops
      if (!preventFeedbackRef.current) {
        const videoTime = videoPlayerAPI.getCurrentTime();

        // Skip NaN during player init
        if (!Number.isNaN(videoTime)) {

          if (currentTimeRef.current !== videoTime) {
            currentTimeRef.current = videoTime;
          }

          if (Math.abs(videoTime - currentTime) > 0.250) {
            setCurrentTime(videoTime);
          }
        }
      }

      // Schedule next frame
      rafId = requestAnimationFrame(sync);
    };

    rafId = requestAnimationFrame(sync);

    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [videoPlayerAPI, editorStore, currentTime]);
  
  
  // Initialize selection manager and adapter
  const selectionManager = useRef<TranscriptSelectionManager | null>(null);
  const selectionAdapter = useRef<SelectionServiceAdapter | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  
  // Initialize and clean up selection manager
  useEffect(() => {
    // Only run this effect when dependencies actually change
    const currentContainer = containerRef.current;
    const containerValue = container;
    const selectionService = editorStore.editor?.selectionService;
    
    // Initialize selection manager with container element once it's available
    if (currentContainer && !selectionManager.current && containerValue) {
      // Create the selection manager
      selectionManager.current = new TranscriptSelectionManager(currentContainer);

      // Attach event listeners
      const handleSelectionComplete = (eventType: SelectionEventType, data: {
        selectedElements?: ElementSelectionData[];
        position?: { x: number; y: number };
      }) => {
        if (data?.selectedElements && data.selectedElements.length > 0) {
          // Show speaker selection menu at mouse position
          setSpeakerMenuState({
            isOpen: true,
            position: data.position || { x: 0, y: 0 },
            selectedElements: data.selectedElements
          });
        }
      };
      
      // Listen for right-click events to show context menu
      selectionManager.current.addEventListener('rightClick', handleSelectionComplete);
      
      // If editor store has a selection service, create adapter 
      if (selectionService) {
        selectionAdapter.current = new SelectionServiceAdapter(
          selectionManager.current,
          selectionService,
          containerValue
        );
      }
    }
    
    // Clean up selection manager on unmount
    return () => {
      if (selectionAdapter.current) {
        selectionAdapter.current.cleanup();
        selectionAdapter.current = null;
      }
      
      if (selectionManager.current) {
        selectionManager.current.cleanup();
        selectionManager.current = null;
      }
    };
  }, [container, editorStore.editor?.selectionService]);

  useEffect(() => {
    if (!container) return;
    scrollToActiveParagraph(currentTime);
  }, [container, currentTime]);
  
  // Define element click handler outside of the render function
  const handleElementClick = useCallback(
    (e: React.MouseEvent, elementId: string, element: HTMLElement, startTime?: number) => {
      if (startTime !== undefined) {
        // Seek to the timestamp when clicking on text
        jumpToTimestamp(startTime);
      }
    },
    [jumpToTimestamp]
  );

  // ------------------------------------------------------------
  //   HIGH-FREQUENCY WORD HIGHLIGHTER (DOM-only, no React work)
  // ------------------------------------------------------------
  useEffect(() => {
    if (!container) return;

    // Flat list of *text* elements with their start times
    // We build it once; the data model isn't huge.
    type WordEntry = {
      id: string;
      start: number;
      end: number;
      dom: HTMLElement | null;
    };

    const words: WordEntry[] = [];
    container.segments.forEach(seg =>
      seg.paragraphs.forEach(p =>
        p.elements.forEach(el => {
          if (isTextElement(el)) {
            words.push({
              id: el.id,
              start: el.startTime ?? 0,
              end:
                el.endTime && el.endTime > (el.startTime ?? 0)
                  ? el.endTime
                  : (el.startTime ?? 0) + 0.05, // 50 ms fallback
              dom: null, // we fill this lazily
            });
          }
        })
      )
    );

    // Sort once so we can binary-search
    words.sort((a, b) => a.start - b.start);

    let raf: number;
    let lastIdx = -1;

    const highlight = () => {
      const t = currentTimeRef.current; // see note below

      // Check if user selection is active before highlighting
      const hasUserSelection = false;//document.querySelector('.transcript-element.selected') !== null;
    
      // ----- binary search for active word --------------------
      let lo = 0,
        hi = words.length - 1,
        idx = -1;
        
      while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        if (t < words[mid].start) {
          hi = mid - 1;
        } else if (t >= words[mid].end) {
          lo = mid + 1;
        } else {
          idx = mid;
          break;
        }
      }
    
      // Only proceed with word highlighting if no user selection is active
      if (!hasUserSelection) {
        if (idx !== lastIdx) {
          // Remove all highlights
          document.querySelectorAll('.selected').forEach(el => {
            el.classList.remove('selected');
          });

          // Add new highlight
          if (idx !== -1) {
            const cur = words[idx];
            const newElement = cur.dom ?? document.querySelector(
              `[data-element-id="${cur.id}"]`
            ) as HTMLElement;
            
            if (newElement) {
              // Only add active-word class if this element isn't also selected by user
              if (!newElement.classList.contains('selected')) {
                newElement.classList.add('selected');
              }
              cur.dom = newElement;
            }
          }
        }
      } else {
        // Clear any existing active word highlight when user selection exists
        if (lastIdx !== -1) {
          const old = words[lastIdx];
          const oldElement = old.dom ?? document.querySelector(
            `[data-element-id="${old.id}"]`
          ) as HTMLElement;
          
          if (oldElement) {
            oldElement.classList.remove('selected');
            old.dom = oldElement;
          }
        }
      }

      // Update the last index
      lastIdx = hasUserSelection ? -1 : idx;
      
      // Continue the animation
      raf = requestAnimationFrame(highlight);
    };

    raf = requestAnimationFrame(highlight);

    return () => cancelAnimationFrame(raf);
  }, [container]);

  // Compute list of segments that contain at least one visible paragraph
  const segmentsToRender = useMemo(() => {
    if (!container) return [];
    if (!visibleParagraphIds) return container.segments;

    return container.segments.filter(seg => seg.paragraphs.some(p => visibleParagraphIds.has(p.id)));
  }, [container?.segments, container?.segments.length, container?.counter, visibleParagraphIds, ...container?.segments.map(s => s.counter) || []]);

  if (!container) {
    return <div className="loading">Loading transcript...</div>;
  }

  return (
    <Container 
      ref={containerRef}
      className={className || ''}
      // We need contentEditable for proper selection behavior
      contentEditable="true"
      suppressContentEditableWarning={true}
    >
      <TranscriptScrollContainer className="transcript-scroll-container">
        <div className="transcript-content">
          {/* Use memoized segment wrappers to prevent re-renders of all paragraphs */}
          {segmentsToRender.map(segment => (
            <TranscriptSegmentWrapper
              key={`segment-${segment.id}`}
              segment={segment}
              segmentId={segment.id}
              editorStore={editorStore}
              counter={segment.counter}
              selectionManager={selectionManager.current}
              handleElementClick={handleElementClick}
              searchTerm={searchTerm || ''}
              visibleParagraphIds={visibleParagraphIds}
            />
          ))}
        </div>
      </TranscriptScrollContainer>
      
      {/* Speaker selection menu for text selections */}
      <SpeakerSelectionMenu
        key="speaker-selection-menu"
        isOpen={speakerMenuState.isOpen}
        position={speakerMenuState.position}
        editorStore={editorStore}
        onClose={() => setSpeakerMenuState(prev => ({ ...prev, isOpen: false }))}
        onCreateNewSpeaker={() => {
          // Close the speaker menu - selection is now preserved by SelectionService
          setSpeakerMenuState(prev => ({ ...prev, isOpen: false }));
          // Show the new speaker modal
          setShowNewSpeakerModal(true);
        }}
        onFineAlign={onFineAlign}
        onForceAlign={onForceAlign}
        onRequestGlobalReplace={(text) => {
          // Close the speaker menu
          setSpeakerMenuState(prev => ({ ...prev, isOpen: false }));
          // Open the global replace modal
          setGlobalReplaceModalState({
            isOpen: true,
            originalText: text
          });
        }}
      />
      
      {/* Modal for creating new speakers - fully self-contained via SelectionService */}
      {showNewSpeakerModal && (
        <SpeakerFormModal
          key="new-speaker-modal"
          isOpen={showNewSpeakerModal}
          onClose={() => {
            setShowNewSpeakerModal(false);
            setSpeakerMenuState(prev => ({ ...prev, isOpen: false }));
          }}
          editorStore={editorStore}
        />
      )}
      
      {/* Global text replacement modal */}
      <GlobalReplaceModal
        key="global-replace-modal"
        isOpen={globalReplaceModalState.isOpen}
        onClose={() => setGlobalReplaceModalState(prev => ({ ...prev, isOpen: false }))}
        originalText={globalReplaceModalState.originalText}
        editorStore={editorStore}
        // This is currently commented out in the code, but we provide a noop function
        // to make TypeScript happy and not error about the unused prop
        onReplace={() => Promise.resolve()}
      />
     
    </Container>
  );
});

// Styled components
const Container = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  user-select: text;
  
  /* Important for cross-element selection */
  ::selection {
    background-color: transparent;
    color: ${defaultTheme.colors.text};
  }
  
  /* Make non-editable elements not selectable */
  & .non-editable,
  & .non-editable * {
    user-select: none !important;
  }
  
  /* Keep dropdowns functional */
  & select {
    pointer-events: auto !important;
  }
  
  /* Fix selection appearance */
  :focus {
    outline: none;
  }
`;

/**
 * Scrollable container for transcript content
 */
export const TranscriptScrollContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  margin-top: 7px;

  /* Important for cross-segment selection */
  user-select: text;
  cursor: text;
  
  ::selection {
    background-color: transparent;
    color: ${defaultTheme.colors.text};
  }
  
  :focus {
    outline: none;
  }
  
  /* Style for transcript elements */
  & .transcript-element {
    cursor: text;
    
    /* Slightly enhance hover/focus states */
    &:hover {
      color: lightgreen;
    }
    
    &:focus {
      background-color: rgba(242, 208, 147, 0.15);
      outline: none;
    }
  }
`;

export default TranscriptView;